/**
 * Date utilities: formatting, ranges, and regex patterns.
 */
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { env } from '#core/lib/utils/env.ts';

// Idempotent — `init()` in core/main.tsx also extends these for the app.
// Loading them here lets `formatDate`/`formatDateTime` work in unit tests
// (which don't run `init()`) and in any module that imports them before app boot.
dayjs.extend(utc);
dayjs.extend(timezone);

export const formatDate = (date?: Date | string) => (date ? dayjs(date).tz(env.displayTz).format('YYYY-MM-DD') : 'n/a');

export const formatDateTime = (date?: Date | string) =>
  date ? dayjs(date).tz(env.displayTz).format('YYYY-MM-DD HH:mm') : 'n/a';

export function* getDateRange(start: dayjs.Dayjs, end: dayjs.Dayjs, unit: dayjs.ManipulateType) {
  let current = start.clone().startOf(unit).subtract(1, unit);
  const last = end.subtract(1, unit);
  while (current < last) {
    current = current.add(1, unit);
    yield current;
  }
  return false;
}

/**
 * Strict ISO 8601 date pattern.
 * Matches: YYYY-MM-DD with optional time component.
 * Examples: "2024-01-15", "2024-01-15T10:30:00", "2024-01-15T10:30:00.123Z", "2024-01-15T10:30:00+02:00"
 */
export const ISO_DATE_REGEX_STRICT = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * Basic ISO 8601 date pattern.
 * Matches strings that start with YYYY-MM-DD format.
 * Used for quick type detection where full validation isn't needed.
 */
export const ISO_DATE_REGEX_BASIC = /^\d{4}-\d{2}-\d{2}/;
/**
 * Soft-parse a value as a date — returns a {@link dayjs.Dayjs} on success,
 * `null` on failure. Accepts strings, numbers (Unix milliseconds), and
 * `Date` objects.
 *
 * Use when the caller has a sensible fallback for invalid input — filter
 * predicates that should silently reject malformed cells, or normalisers
 * that pass through unparseable values (e.g. CDF's relative-time strings
 * like `"30d-ago"` are intentionally `null` here so callers can leave them
 * untouched for server-side resolution).
 */
export function tryParseDate(value: unknown): dayjs.Dayjs | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date || typeof value === 'string' || typeof value === 'number') {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  return null;
}

/**
 * Strict-parse a value as a date — throws on invalid input. Use when the
 * caller's contract guarantees a valid date and silently substituting
 * `null` would corrupt downstream computation (e.g. user-supplied date
 * arguments to derive expressions).
 */
export function parseDate(value: unknown): dayjs.Dayjs {
  const d = tryParseDate(value);
  if (d === null) {
    throw new Error('Invalid date format. Use ISO 8601 (YYYY-MM-DD)');
  }
  return d;
}
